import { useState } from "react";
import { LayoutFormCollaborator } from "./form-collaborator";
import { useFormik } from "formik";
import { useCollaboratorOnboardingSchema } from "./collaborator-onboarding-schema";
import { EmployeeRequestDTO } from "@/types";
import {
  useCreateEmployeeMutation,
  useCreateRoleMutation,
  useFetchRolesQuery,
  usePermission,
  useToast,
} from "@/hooks";
import { useTranslation } from "react-i18next";

import {
  TaxIdInput,
  Label,
  Input,
  MultiSelect,
  Option,
  Button,
} from "@/components/ui";
import { DisplayError } from "@/utils";
import { ImportEmployeeModal } from "./import-employee-modal";

type FormCreateCollaboratorProps = {
  handleClose: () => void;
};




export function FormCreateCollaborator({
  handleClose,
}: FormCreateCollaboratorProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);


  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { data: rolesData, isLoading: isLoadingRoles } = useFetchRolesQuery({});
  const { mutateAsync, isLoading } = useCreateEmployeeMutation();

  // O-17: the cargo is a dependency of this form. Creating one used to mean
  // leaving a half-filled professional behind.
  const { checkPermission } = usePermission();
  const canCreateRole = checkPermission("positions", "create positions");
  const { mutateAsync: createRole } = useCreateRoleMutation();
  const [createdRoles, setCreatedRoles] = useState<Option[]>([]);
  const [roleCreateError, setRoleCreateError] = useState<string | null>(null);

  async function handleCreateRole(name: string): Promise<Option> {
    setRoleCreateError(null);
    try {
      const { data } = await createRole({ name });
      const option: Option = { value: data.id, label: data.name };
      setCreatedRoles((current) => [...current, option]);
      return option;
    } catch (error) {
      const message = DisplayError(error, t("MESSAGE.error_message"));
      setRoleCreateError(message);
      toast({
        title: t("LABEL.error"),
        description: message,
        variant: "destructive",
      });
      throw error;
    }
  }

  const validationSchema = useCollaboratorOnboardingSchema();

  const initialValues = {
    name: "",
    email: "",
    cpf: "",
    phone: "",
    crm: "",
    positionsId: "",
    address: "",
    interval: "",
    payment: "",
    avatar: "",
  };

  const { handleSubmit, setSubmitting, ...formik } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (employeeData) => {
      createNewEmployee(employeeData);
    },
  });

  async function createNewEmployee(employeeData: EmployeeRequestDTO) {
    try {
      const fd = new FormData();
      fd.append("name", employeeData.name || "");
      fd.append("email", employeeData.email || "");
      fd.append("cpf", employeeData.cpf || "");
      fd.append("phone", employeeData.phone);
      fd.append("crm", employeeData.crm || "");
      fd.append("positionsId", employeeData.positionsId || "");
      fd.append("address", employeeData.address || "");
      fd.append("interval", employeeData.interval || "");
      fd.append("payment", employeeData.payment || "");
      fd.append("avatar", file || "");
      await mutateAsync(fd, {
        onSuccess: () => {
          handleClose();
          formik.resetForm();
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.created successfully"),
          });
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files && event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          setPreviewUrl(reader.result);
        }
      };
      reader.readAsDataURL(selectedFile);
    }
  };
  return (
    <form onSubmit={handleSubmit} className="h-full min-h-0">
      <LayoutFormCollaborator
        handleImageChange={handleImageChange}
        previewUrl={previewUrl}
        formik={formik}
        headerButton={
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsImportModalOpen(true)}
            className="text-sm"
          >
            Importar CSV
          </Button>
        }
      >
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="p-4 space-y-1">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div className="space-y-1">
                <Label required>{t("LABEL.name")}</Label>
                <Input
                  isInvalid={formik.touched.name && formik.errors.name}
                  isValid={formik.touched.name && !formik.errors.name}
                  {...formik.getFieldProps("name")}
                  messageError={formik.errors.name}
                />
              </div>
              <div className="space-y-1">
                <Label required>{t("LABEL.email")}</Label>
                <Input
                  isInvalid={formik.touched.email && formik.errors.email}
                  isValid={formik.touched.email && !formik.errors.email}
                  {...formik.getFieldProps("email")}
                  messageError={formik.errors.email}
                />
              </div>
              <div className="space-y-1">
                <Label>{t("LABEL.cpf")} </Label>
                <TaxIdInput
                  typeMask="cpf"
                  {...formik.getFieldProps("cpf")}
                  isInvalid={formik.touched.cpf && formik.errors.cpf}
                  isValid={formik.touched.cpf && !formik.errors.cpf}
                  messageError={formik.errors.cpf}
                />
              </div>
              <div className="space-y-1">
                <Label required>{t("LABEL.phone")}</Label>
                <Input
                  inputPhone
                  isInvalid={formik.touched.phone && formik.errors.phone}
                  isValid={formik.touched.phone && !formik.errors.phone}
                  {...formik.getFieldProps("phone")}
                  messageError={formik.errors.phone}
                />
              </div>
              <div className="space-y-1">
                <Label>Registro profissional (CRM/CRO/outro)</Label>
                <Input
                  isInvalid={formik.touched.crm && formik.errors.crm}
                  isValid={formik.touched.crm && !formik.errors.crm}
                  {...formik.getFieldProps("crm")}
                  messageError={formik.errors.crm}
                />
              </div>
              <div className="space-y-1">
                <Label required>{t("LABEL.role")}</Label>
                <div className="space-y-1">
                  <MultiSelect
                    options={
                      rolesData?.items.map((role) => ({
                        value: role.id,
                        label: role.name,
                      })) || []
                    }
                    selectedOptions={createdRoles}
                    loading={isLoadingRoles}
                    isInvalid={
                      Boolean(roleCreateError) ||
                      (formik.touched.positionsId && formik.errors.positionsId)
                    }
                    isValid={
                      !roleCreateError &&
                      formik.touched.positionsId &&
                      !formik.errors.positionsId
                    }
                    placeholder="Selecione um cargo..."
                    emptyMessage="Nenhum cargo encontrado"
                    width="300px"
                    value={formik.values.positionsId}
                    onChange={(value) => {
                      setRoleCreateError(null);
                      formik.setFieldValue("positionsId", value);
                    }}
                    onBlur={() => formik.setFieldTouched("positionsId", true)}
                    messageError={roleCreateError ?? formik.errors.positionsId}
                    allowCreate={canCreateRole}
                    onCreateOption={handleCreateRole}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label>{t("LABEL.address")} (opcional)</Label>
                <Input
                  isInvalid={formik.touched.address && formik.errors.address}
                  isValid={formik.touched.address && !formik.errors.address}
                  {...formik.getFieldProps("address")}
                  messageError={formik.errors.address}
                />
              </div>
              {/* <div className="space-y-1">
                <Label>{t("LABEL.interval between consultations")}</Label>
                <Input
                  isInvalid={formik.touched.interval && formik.errors.interval}
                  isValid={formik.touched.interval && !formik.errors.interval}
                  {...formik.getFieldProps("interval")}
                  messageError={formik.errors.interval}
                />
              </div> */}
              <div className="space-y-1">
                <Label>{t("LABEL.pix key")} (opcional)</Label>
                <Input
                  isInvalid={formik.touched.payment && formik.errors.payment}
                  isValid={formik.touched.payment && !formik.errors.payment}
                  {...formik.getFieldProps("payment")}
                  messageError={formik.errors.payment}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-center gap-2 border-t p-4 pb-6">
          <Button type="submit" className="px-10" isLoading={isLoading}>
            {t("LABEL.save")}
          </Button>
        </div>
      </LayoutFormCollaborator>
      
      <ImportEmployeeModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={handleClose}
      />
    </form>
  );
}
